import {
  useCarrierRatio,
  useForceRetryByCategory,
  useResolveByCategory,
  useResolveByShopCarrier,
  useTrackingErrors,
  useTrackingErrorsSummary,
  type CarrierRatioEntry,
  type TopShopEntry,
  type TrackingErrorsFilters,
  type TrackingJobError,
} from "@/api/super-admin/use-tracking-queue-errors";
import {
  AlertTriangle,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  RefreshCw,
  Truck,
  X,
} from "lucide-react";
import { useState } from "react";

// ─── Severity badge ────────────────────────────────────────────────────────────

const SEVERITY_CONFIG: Record<
  string,
  { label: string; bg: string; text: string }
> = {
  actionable: {
    label: "Actionable",
    bg: "bg-red-50",
    text: "text-red-700",
  },
  config: { label: "Config", bg: "bg-orange-50", text: "text-orange-700" },
  transient: {
    label: "Transient",
    bg: "bg-yellow-50",
    text: "text-yellow-700",
  },
  pending: {
    label: "Pending",
    bg: "bg-blue-50",
    text: "text-blue-600",
  },
};

function SeverityBadge({ severity }: { severity: string }) {
  const cfg = SEVERITY_CONFIG[severity] ?? {
    label: severity,
    bg: "bg-gray-100",
    text: "text-gray-600",
  };
  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${cfg.bg} ${cfg.text}`}
    >
      {cfg.label}
    </span>
  );
}

// ─── Category badge ────────────────────────────────────────────────────────────

const CATEGORY_COLORS: Record<string, string> = {
  BAD_REQUEST: "text-red-600",
  UNAUTHORIZED: "text-orange-600",
  FORBIDDEN: "text-orange-600",
  RATE_LIMITED: "text-yellow-600",
  CARRIER_NOT_FOUND: "text-purple-600",
  CARRIER_NOT_FOUND_FOR_SHOP: "text-purple-600",
  CARRIER_PREFERENCES_NOT_FOUND: "text-purple-600",
  NO_DATA_FOUND: "text-blue-500",
  PARCEL_NOT_FOUND: "text-blue-500",
  TRANSIENT_NETWORK_ERROR: "text-blue-400",
  INVALID_TRACKING_ID: "text-red-500",
  UNKNOWN: "text-gray-500",
};

// ─── Summary cards ─────────────────────────────────────────────────────────────

function SummaryCards({
  total,
  bySeverity,
}: {
  total: number;
  bySeverity: Array<{ errorSeverity: string; count: number }>;
}) {
  const actionable =
    bySeverity.find((s) => s.errorSeverity === "actionable")?.count ?? 0;
  const config =
    bySeverity.find((s) => s.errorSeverity === "config")?.count ?? 0;
  const transient =
    bySeverity.find((s) => s.errorSeverity === "transient")?.count ?? 0;
  const pending =
    bySeverity.find((s) => s.errorSeverity === "pending")?.count ?? 0;

  return (
    <div className="grid grid-cols-5 gap-4">
      {[
        { label: "Total erreurs", value: total, color: "text-foreground" },
        { label: "Actionable", value: actionable, color: "text-red-600" },
        { label: "Config", value: config, color: "text-orange-600" },
        { label: "Transient", value: transient, color: "text-yellow-600" },
        {
          label: "Pending",
          value: pending,
          color: "text-blue-600",
          title:
            "Colis pas encore expédiés / pas encore dans le système carrier",
        },
      ].map(({ label, value, color, title = undefined }) => (
        <div
          key={label}
          title={title}
          className="rounded-lg border bg-background p-4 flex flex-col gap-1"
        >
          <span className="text-xs text-muted-foreground font-medium">
            {label}
          </span>
          <span className={`text-2xl font-bold tabular-nums ${color}`}>
            {value.toLocaleString("fr-FR")}
          </span>
        </div>
      ))}
    </div>
  );
}

// ─── Single error row ──────────────────────────────────────────────────────────

function ErrorRow({ err }: { err: TrackingJobError }) {
  return (
    <div className="px-4 py-3 space-y-1.5">
      {err.errorClass && (
        <span className="text-[10px] font-mono bg-muted px-1.5 py-0.5 rounded text-muted-foreground">
          {err.errorClass}
        </span>
      )}
      <p className="text-xs text-muted-foreground break-words">
        {err.errorMessage}
      </p>
      <div className="flex items-center gap-3 flex-wrap text-[10px] text-muted-foreground/70">
        <span className="font-mono">{err.trackingCode}</span>
        <span>{new Date(err.failedAt).toLocaleDateString("fr-FR")}</span>
        {err.attemptsMade > 0 && (
          <span title="Nombre de tentatives">
            🔁 {err.attemptsMade} tentative{err.attemptsMade > 1 ? "s" : ""}
          </span>
        )}
        {err.returnRequestId && (
          <a
            href={`/shops/${err.shopId}/returns/${err.returnRequestId}`}
            target="_blank"
            rel="noopener noreferrer"
            className="font-mono underline underline-offset-2 hover:text-foreground transition-colors"
            title="Voir la demande de retour"
          >
            {err.returnRequestId.slice(0, 8)}&hellip;
          </a>
        )}
      </div>
    </div>
  );
}

// ─── Category group within a carrier ──────────────────────────────────────────

function CategoryGroup({
  shopId,
  carrierCode,
  errorCategory,
  errorSeverity,
  errors,
  onResolved,
}: {
  shopId: string;
  carrierCode: string;
  errorCategory: string;
  errorSeverity: string;
  errors: TrackingJobError[];
  onResolved: () => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const resolveByCategory = useResolveByCategory();
  const forceRetryByCategory = useForceRetryByCategory();
  const PREVIEW = 3;

  // Deduplicate by errorMessage to surface unique patterns in preview
  const uniqueMessages = Array.from(
    new Map(errors.map((e) => [e.errorMessage, e])).values(),
  );

  async function handleResolve() {
    await resolveByCategory.mutateAsync({
      shopId,
      carrierCode,
      errorCategory,
      note: "Resolved via super-admin carrier issues tab",
    });
    onResolved();
  }

  async function handleForceRetry() {
    await forceRetryByCategory.mutateAsync({
      shopId,
      carrierCode,
      errorCategory,
    });
  }

  return (
    <div className="border-t first:border-t-0">
      {/* Category header row */}
      <div className="flex items-center justify-between px-4 py-2.5 bg-muted/20 hover:bg-muted/30 transition-colors">
        <button
          className="flex items-center gap-2 flex-1 text-left"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
          )}
          <SeverityBadge severity={errorSeverity} />
          <span
            className={`text-xs font-mono font-semibold ${CATEGORY_COLORS[errorCategory] ?? "text-gray-500"}`}
          >
            {errorCategory}
          </span>
          <span className="text-xs text-muted-foreground">
            {errors.length} erreur{errors.length > 1 ? "s" : ""}
            {uniqueMessages.length < errors.length && (
              <span className="ml-1 opacity-60">
                &middot; {uniqueMessages.length} pattern
                {uniqueMessages.length > 1 ? "s" : ""} distinct
                {uniqueMessages.length > 1 ? "s" : ""}
              </span>
            )}
          </span>
        </button>
        <div className="flex items-center gap-1 shrink-0">
          <button
            onClick={() => void handleForceRetry()}
            disabled={forceRetryByCategory.isPending}
            className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-md bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100 transition-colors disabled:opacity-50"
          >
            <RefreshCw className="h-3 w-3" />
            Retry ({errors.length})
          </button>
          <button
            onClick={() => void handleResolve()}
            disabled={resolveByCategory.isPending}
            className="flex items-center gap-1 px-2 py-1 text-[10px] rounded-md bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors disabled:opacity-50"
          >
            <CheckCircle2 className="h-3 w-3" />
            Résoudre ({errors.length})
          </button>
        </div>
      </div>

      {/* Preview (collapsed): unique messages only */}
      {!expanded && uniqueMessages.length > 0 && (
        <div className="divide-y border-t bg-background/50">
          {uniqueMessages.slice(0, PREVIEW).map((err) => (
            <ErrorRow key={err.id} err={err} />
          ))}
          {errors.length > PREVIEW && (
            <button
              className="w-full px-4 py-2 text-xs text-muted-foreground hover:text-foreground text-left transition-colors"
              onClick={() => setExpanded(true)}
            >
              +{errors.length - PREVIEW} erreurs &middot; voir tout
            </button>
          )}
        </div>
      )}

      {/* Expanded: all rows */}
      {expanded && (
        <div className="divide-y border-t bg-background/50">
          {errors.map((err) => (
            <ErrorRow key={err.id} err={err} />
          ))}
        </div>
      )}
    </div>
  );
}

// ─── Error detail drawer for a shop ────────────────────────────────────────────

function ShopErrorDetail({
  shopId,
  shopName,
  onClose,
}: {
  shopId: string;
  shopName: string;
  onClose: () => void;
}) {
  const [filters, setFilters] = useState<TrackingErrorsFilters>({
    shopId,
    isResolved: false,
    page: 1,
    pageSize: 200,
  });
  const { data, isPending, refetch } = useTrackingErrors(filters);
  const resolveByShop = useResolveByShopCarrier();
  const { data: ratioData } = useCarrierRatio(shopId);
  const ratioByCarrier = new Map<string, CarrierRatioEntry>(
    (ratioData ?? []).map((r) => [r.carrierCode, r]),
  );

  // carrier → category → { severity, errors[] }
  const byCarrier = new Map<
    string,
    Map<string, { severity: string; errors: TrackingJobError[] }>
  >();
  for (const err of data?.data ?? []) {
    if (!byCarrier.has(err.carrierCode))
      byCarrier.set(err.carrierCode, new Map());
    const carrierMap = byCarrier.get(err.carrierCode)!;
    if (!carrierMap.has(err.errorCategory))
      carrierMap.set(err.errorCategory, {
        severity: err.errorSeverity,
        errors: [],
      });
    carrierMap.get(err.errorCategory)!.errors.push(err);
  }

  async function handleResolveCarrier(carrierCode: string) {
    await resolveByShop.mutateAsync({
      shopId,
      carrierCode,
      note: "Resolved via super-admin carrier issues tab",
    });
    void refetch();
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      <div className="flex-1 bg-black/30 backdrop-blur-sm" onClick={onClose} />
      <div className="w-[720px] bg-background border-l shadow-xl flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <div>
            <h3 className="font-semibold">{shopName}</h3>
            <p className="text-xs text-muted-foreground">
              Erreurs de tracking non résolues
            </p>
          </div>
          <button
            onClick={onClose}
            className="rounded-md p-1 hover:bg-muted transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        {/* Filters */}
        <div className="px-6 py-3 border-b flex items-center gap-3">
          <label className="flex items-center gap-2 text-xs text-muted-foreground">
            <input
              type="checkbox"
              checked={filters.isResolved === undefined}
              onChange={(e) =>
                setFilters((f) => ({
                  ...f,
                  isResolved: e.target.checked ? undefined : false,
                }))
              }
            />
            Tout afficher (incl. résolus)
          </label>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4 space-y-4">
          {isPending && (
            <div className="text-sm text-muted-foreground animate-pulse">
              Chargement…
            </div>
          )}

          {!isPending && data?.total === 0 && (
            <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
              <CheckCircle2 className="h-8 w-8 text-green-500" />
              <p className="text-sm">Aucune erreur non résolue</p>
            </div>
          )}

          {/* Grouped: carrier → category sub-groups */}
          {Array.from(byCarrier.entries()).map(([carrierCode, categories]) => {
            const totalCarrier = Array.from(categories.values()).reduce(
              (s, g) => s + g.errors.length,
              0,
            );
            const ratio = ratioByCarrier.get(carrierCode);
            const errorPct =
              ratio && ratio.totalRequests > 0
                ? Math.round(ratio.errorRate * 100)
                : null;
            return (
              <div
                key={carrierCode}
                className="rounded-lg border overflow-hidden"
              >
                {/* Carrier header */}
                <div className="flex items-center justify-between px-4 py-3 bg-muted/40">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Truck className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium text-sm">{carrierCode}</span>
                    <span className="text-xs text-muted-foreground">
                      ({totalCarrier} erreur{totalCarrier > 1 ? "s" : ""}{" "}
                      &middot; {categories.size} type
                      {categories.size > 1 ? "s" : ""})
                    </span>
                    {errorPct !== null && (
                      <span
                        className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-semibold ${
                          errorPct >= 50
                            ? "bg-red-100 text-red-700"
                            : errorPct >= 20
                              ? "bg-orange-100 text-orange-700"
                              : errorPct >= 5
                                ? "bg-yellow-100 text-yellow-700"
                                : "bg-green-100 text-green-700"
                        }`}
                        title={`${ratio!.errorRequests} retours en erreur / ${ratio!.totalRequests} retours trackés`}
                      >
                        {errorPct}% d&apos;erreur
                      </span>
                    )}
                  </div>
                  <button
                    onClick={() => void handleResolveCarrier(carrierCode)}
                    disabled={resolveByShop.isPending}
                    className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-green-50 text-green-700 border border-green-200 hover:bg-green-100 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle2 className="h-3 w-3" />
                    Tout résoudre
                  </button>
                </div>

                {/* Category sub-groups */}
                {Array.from(categories.entries()).map(
                  ([errorCategory, { severity, errors }]) => (
                    <CategoryGroup
                      key={errorCategory}
                      shopId={shopId}
                      carrierCode={carrierCode}
                      errorCategory={errorCategory}
                      errorSeverity={severity}
                      errors={errors}
                      onResolved={() => void refetch()}
                    />
                  ),
                )}
              </div>
            );
          })}
        </div>

        {/* Footer */}
        {data && data.total > 0 && (
          <div className="px-6 py-3 border-t bg-muted/20 text-xs text-muted-foreground">
            {data.total} erreur{data.total > 1 ? "s" : ""}
            {filters.isResolved === false ? " (non résolues)" : ""}
          </div>
        )}
      </div>
    </div>
  );
}

// ─── Top shops inline breakdown ───────────────────────────────────────────────

function TopShopsList({
  topShops,
  shopNameById,
  onSelectShop,
}: {
  topShops: TopShopEntry[];
  shopNameById: Map<string, string>;
  onSelectShop: (id: string, name: string) => void;
}) {
  return (
    <div className="border-t divide-y bg-muted/10">
      {topShops.map(({ shopId, count }) => {
        const name = shopNameById.get(shopId) ?? shopId.slice(0, 8) + "…";
        return (
          <div
            key={shopId}
            className="flex items-center justify-between pl-8 pr-4 py-1.5"
          >
            <button
              className="text-xs text-left hover:underline truncate max-w-[200px]"
              onClick={() => onSelectShop(shopId, name)}
              title={`Gérer les erreurs de ${name}`}
            >
              {name}
            </button>
            <span className="text-xs font-semibold tabular-nums text-muted-foreground">
              {count.toLocaleString("fr-FR")}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ─── Main tab ─────────────────────────────────────────────────────────────────

export function CarrierIssuesTab({
  shops,
}: {
  shops: Array<{ id: string; name: string; slug: string }>;
}) {
  const [showResolved, setShowResolved] = useState(false);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [selectedShop, setSelectedShop] = useState<{
    id: string;
    name: string;
  } | null>(null);

  const {
    data: summary,
    isPending,
    refetch,
  } = useTrackingErrorsSummary({
    isResolved: showResolved ? undefined : false,
  });
  const { data: globalRatioData } = useCarrierRatio();
  const globalRatioByCarrier = new Map<string, CarrierRatioEntry>(
    (globalRatioData ?? []).map((r) => [r.carrierCode, r]),
  );

  const shopNameById = new Map(shops.map((s) => [s.id, s.name]));

  return (
    <div className="space-y-6">
      {/* Toolbar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-base font-semibold">
            Erreurs de tracking par shop
          </h2>
          <label className="flex items-center gap-1.5 text-xs text-muted-foreground cursor-pointer select-none">
            <input
              type="checkbox"
              className="rounded"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
            />
            Inclure les résolus
          </label>
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={() => void refetch()}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md border hover:bg-muted transition-colors"
          >
            <RefreshCw className="h-3 w-3" />
            Actualiser
          </button>
        </div>
      </div>

      {isPending && (
        <div className="text-sm text-muted-foreground animate-pulse">
          Chargement…
        </div>
      )}

      {summary && (
        <>
          {/* Summary cards */}
          <SummaryCards total={summary.total} bySeverity={summary.bySeverity} />

          {/* Category + Carrier breakdown */}
          <div className="grid grid-cols-2 gap-4">
            {/* By error category */}
            <div className="rounded-lg border bg-background overflow-hidden">
              <div className="px-4 py-3 border-b">
                <span className="text-sm font-medium">Par catégorie</span>
              </div>
              <div className="divide-y">
                {summary.byErrorCategory.map(
                  ({ errorCategory, count, topShops }) => {
                    const key = `cat-${errorCategory}`;
                    const isOpen = expandedRow === key;
                    return (
                      <div key={errorCategory}>
                        <button
                          className="w-full flex items-center justify-between px-4 py-2 hover:bg-muted/30 transition-colors"
                          onClick={() => setExpandedRow(isOpen ? null : key)}
                        >
                          <div className="flex items-center gap-1.5">
                            {isOpen ? (
                              <ChevronDown className="h-3 w-3 text-muted-foreground" />
                            ) : (
                              <ChevronRight className="h-3 w-3 text-muted-foreground" />
                            )}
                            <span
                              className={`text-xs font-mono ${CATEGORY_COLORS[errorCategory] ?? "text-gray-500"}`}
                            >
                              {errorCategory}
                            </span>
                          </div>
                          <span className="text-sm font-semibold tabular-nums">
                            {count.toLocaleString("fr-FR")}
                          </span>
                        </button>
                        {isOpen && (
                          <TopShopsList
                            topShops={topShops}
                            shopNameById={shopNameById}
                            onSelectShop={(id, name) =>
                              setSelectedShop({ id, name })
                            }
                          />
                        )}
                      </div>
                    );
                  },
                )}
              </div>
            </div>

            {/* By carrier */}
            <div className="rounded-lg border bg-background overflow-hidden">
              <div className="flex items-center px-4 py-3 border-b">
                <span className="text-sm font-medium">Par carrier</span>
              </div>
              <div className="divide-y">
                {summary.byCarrier.map(({ carrierCode, count, topShops }) => {
                  const ratio = globalRatioByCarrier.get(carrierCode);
                  const errorPct =
                    ratio && ratio.totalRequests > 0
                      ? Math.round(ratio.errorRate * 100)
                      : null;
                  const key = `carrier-${carrierCode}`;
                  const isOpen = expandedRow === key;
                  return (
                    <div key={carrierCode}>
                      <button
                        className="w-full flex items-center justify-between px-4 py-2 gap-3 hover:bg-muted/30 transition-colors"
                        onClick={() => setExpandedRow(isOpen ? null : key)}
                      >
                        <div className="flex items-center gap-1.5">
                          {isOpen ? (
                            <ChevronDown className="h-3 w-3 text-muted-foreground" />
                          ) : (
                            <ChevronRight className="h-3 w-3 text-muted-foreground" />
                          )}
                          <span className="text-xs font-mono text-muted-foreground">
                            {carrierCode}
                          </span>
                        </div>
                        <div className="flex items-center gap-2 shrink-0">
                          {errorPct !== null && (
                            <span
                              className={`text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                                errorPct >= 50
                                  ? "bg-red-100 text-red-700"
                                  : errorPct >= 20
                                    ? "bg-orange-100 text-orange-700"
                                    : errorPct >= 5
                                      ? "bg-yellow-100 text-yellow-700"
                                      : "bg-green-100 text-green-700"
                              }`}
                              title={`${ratio!.errorRequests} retours en erreur / ${ratio!.totalRequests} retours trackés`}
                            >
                              {errorPct}%
                            </span>
                          )}
                          <span className="text-sm font-semibold tabular-nums">
                            {count.toLocaleString("fr-FR")}
                          </span>
                        </div>
                      </button>
                      {isOpen && (
                        <TopShopsList
                          topShops={topShops}
                          shopNameById={shopNameById}
                          onSelectShop={(id, name) =>
                            setSelectedShop({ id, name })
                          }
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Per-shop table */}
          <div className="rounded-lg border bg-background overflow-hidden">
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <span className="text-sm font-medium">
                Shops avec erreurs{" "}
                <span className="text-muted-foreground font-normal">
                  ({summary.byShop.length})
                </span>
              </span>
            </div>

            {summary.byShop.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-12 text-muted-foreground">
                <CheckCircle2 className="h-8 w-8 text-green-500" />
                <p className="text-sm">Aucune erreur de tracking</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-xs text-muted-foreground border-b bg-muted/20">
                    <th className="text-left px-4 py-2.5 font-medium">Shop</th>
                    <th className="text-left px-4 py-2.5 font-medium">
                      Carrier principal
                    </th>
                    <th className="text-left px-4 py-2.5 font-medium">
                      Erreur principale
                    </th>
                    <th className="text-right px-4 py-2.5 font-medium">
                      Erreurs
                    </th>
                    <th className="px-4 py-2.5" />
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {summary.byShop.map(
                    ({ shopId, count, topError, topCarrier }) => {
                      const shopName =
                        shopNameById.get(shopId) ?? shopId.slice(0, 8) + "…";
                      return (
                        <tr
                          key={shopId}
                          className="hover:bg-muted/30 transition-colors"
                        >
                          <td className="px-4 py-3">
                            <span className="font-medium">{shopName}</span>
                          </td>
                          <td className="px-4 py-3">
                            <span className="text-xs font-mono text-muted-foreground">
                              {topCarrier || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`text-xs font-mono ${CATEGORY_COLORS[topError] ?? "text-gray-500"}`}
                            >
                              {topError || "—"}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <span className="font-semibold tabular-nums text-red-600">
                              {count.toLocaleString("fr-FR")}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-right">
                            <button
                              onClick={() =>
                                setSelectedShop({ id: shopId, name: shopName })
                              }
                              className="flex items-center gap-1 px-2.5 py-1 text-xs rounded-md border hover:bg-muted transition-colors ml-auto"
                            >
                              <AlertTriangle className="h-3 w-3 text-orange-500" />
                              Gérer
                            </button>
                          </td>
                        </tr>
                      );
                    },
                  )}
                </tbody>
              </table>
            )}
          </div>
        </>
      )}

      {/* Shop detail drawer */}
      {selectedShop && (
        <ShopErrorDetail
          shopId={selectedShop.id}
          shopName={selectedShop.name}
          onClose={() => setSelectedShop(null)}
        />
      )}
    </div>
  );
}
